import { Link } from 'react-router-dom';
import { Alert } from '../../types';
import StatusBadge from '../common/StatusBadge';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  Clock,
  BellOff,
} from 'lucide-react';

interface AlertCardProps {
  alert: Alert;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  onSnooze: (id: string, until: string) => void;
  loading?: boolean;
}

const severityIcons = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const severityColors = {
  critical: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
  warning: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20',
  info: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
};

export default function AlertCard({
  alert,
  onAcknowledge,
  onResolve,
  onSnooze,
  loading,
}: AlertCardProps) {
  const Icon = severityIcons[alert.severity];

  const handleSnooze = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    onSnooze(alert.id, tomorrow.toISOString());
  };

  return (
    <div
      className={`card border-l-4 ${severityColors[alert.severity]} overflow-hidden`}
    >
      <div className="p-4">
        <div className="flex items-start">
          <Icon className={`h-6 w-6 flex-shrink-0 ${
            alert.severity === 'critical'
              ? 'text-red-500'
              : alert.severity === 'warning'
              ? 'text-orange-500'
              : 'text-blue-500'
          }`} />
          <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <StatusBadge status={alert.severity} type="severity" />
                <StatusBadge status={alert.status} type="alert" />
              </div>
              <span className="text-xs text-muted-foreground">
                {format(parseISO(alert.createdDate), 'MMM d, yyyy h:mm a')}
              </span>
            </div>

            <h4 className="mt-2 text-sm font-medium text-foreground">
              {alert.title}
            </h4>

            <p className="mt-1 text-sm text-muted-foreground">
              {alert.message}
            </p>

            {alert.actionRequired && (
              <div className="mt-2 p-2 bg-muted rounded text-xs text-foreground">
                <strong>Action:</strong> {alert.actionRequired}
              </div>
            )}

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <Link
                  to={`/clients/${alert.clientId}`}
                  className="hover:text-primary-600 dark:hover:text-primary-400"
                >
                  {alert.clientName}
                </Link>
                {alert.subscriptionId && (
                  <>
                    <span>/</span>
                    <Link
                      to={`/subscriptions/${alert.subscriptionId}`}
                      className="hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      View Subscription
                    </Link>
                  </>
                )}
              </div>

              {alert.status === 'active' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onAcknowledge(alert.id)}
                    disabled={loading}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:hover:bg-yellow-800"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Acknowledge
                  </button>
                  <button
                    onClick={handleSnooze}
                    disabled={loading}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800"
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    Snooze
                  </button>
                  <button
                    onClick={() => onResolve(alert.id)}
                    disabled={loading}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                  >
                    <BellOff className="h-3 w-3 mr-1" />
                    Resolve
                  </button>
                </div>
              )}

              {alert.status === 'acknowledged' && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">
                    Acknowledged by {alert.acknowledgedBy} on{' '}
                    {format(parseISO(alert.acknowledgedDate!), 'MMM d')}
                  </span>
                  <button
                    onClick={() => onResolve(alert.id)}
                    disabled={loading}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                  >
                    <BellOff className="h-3 w-3 mr-1" />
                    Resolve
                  </button>
                </div>
              )}

              {alert.status === 'snoozed' && alert.snoozedUntil && (
                <span className="text-xs text-muted-foreground">
                  Snoozed until {format(parseISO(alert.snoozedUntil), 'MMM d, yyyy')}
                </span>
              )}

              {alert.status === 'resolved' && (
                <span className="text-xs text-muted-foreground">
                  Resolved by {alert.resolvedBy} on{' '}
                  {format(parseISO(alert.resolvedDate!), 'MMM d')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
